<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import {
  postOneTimeCodeRequest,
  postOneTimeCodeVerify
} from '@/authentication/api.ts'
import { useConfirm } from 'primevue/useconfirm'
import ConfirmDialog from 'primevue/confirmdialog'
import InputText from 'primevue/inputtext'
import GbButton from '@/components/GbButton.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'

const props = defineProps<{
  email: string
  gblcs: string
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'login-success'): void
  (e: 'otc-requested', value: boolean): void
}>()

const confirm = useConfirm()
const analytics = getAnalyticsTracker()

const otcCode = ref('')
const otcNotice = ref('')
const otcError = ref('')
const otcCodeRequested = ref(false)
const otcRequestSent = ref(false)
const otcVerifySent = ref(false)
const resendSecondsRemaining = ref(0)
const resendIntervalId = ref<ReturnType<typeof globalThis.setInterval> | null>(null)
const resendCountdownSeconds = 55

function confirmAndSendOneTimeCode() {
  if (resendSecondsRemaining.value > 0) return

  confirm.require({
    group: 'login-modal-otc',
    message: `Send a one-time code to ${props.email}?`,
    accept: () => { void sendOneTimeCode() },
  })
}

async function sendOneTimeCode() {
  otcRequestSent.value = true
  otcCode.value = ''
  otcError.value = ''
  try {
    await postOneTimeCodeRequest(props.email, props.gblcs)
    otcCodeRequested.value = true
    emit('otc-requested', true)
    otcNotice.value = `A sign-in code has been sent to ${props.email} if we found an account with that email.`
    startResendCountdown()
  } catch (e) {
    otcNotice.value = ''
    otcError.value = 'We could not send a one-time code right now.'
  } finally {
    otcRequestSent.value = false
  }
}

async function verifyOneTimeCode() {
  const code = (otcCode.value || '').toString().trim().toUpperCase()
  otcCode.value = code
  otcError.value = ''

  if (!/^([A-Z0-9]{10})$/.test(code)) {
    otcError.value = 'Enter a valid 10-character code.'
    return
  }

  otcVerifySent.value = true
  try {
    await postOneTimeCodeVerify(props.email, code, props.gblcs)
    analytics.sendEvent('Sign In Successful', { product: 'toolkit', ref: 'one_time_code' })
    emit('login-success')
  } catch (e) {
    otcError.value = 'We could not verify your code. Please try again.'
  } finally {
    otcVerifySent.value = false
  }
}

function startResendCountdown() {
  clearResendCountdown()
  resendSecondsRemaining.value = resendCountdownSeconds
  resendIntervalId.value = globalThis.setInterval(() => {
    resendSecondsRemaining.value -= 1
    if (resendSecondsRemaining.value <= 0) {
      clearResendCountdown()
    }
  }, 1000)
}

function clearResendCountdown() {
  if (resendIntervalId.value) {
    clearInterval(resendIntervalId.value)
  }
  resendIntervalId.value = null
  if (resendSecondsRemaining.value < 0) {
    resendSecondsRemaining.value = 0
  }
}


function reset() {
  clearResendCountdown()
  otcCodeRequested.value = false
  emit('otc-requested', false)
  otcCode.value = ''
  otcNotice.value = ''
  otcError.value = ''
  resendSecondsRemaining.value = 0
}

defineExpose({ reset })

onBeforeUnmount(() => {
  clearResendCountdown()
})
</script>

<template>
  <div class="mt-[10px]">
    <div class="flex flex-col items-center">
      <GbButton
        type="button"
        gbTheme="grayOutlineSecondary"
        size="large"
        class="mt-2 mb-4"
        :disabled="disabled || otcRequestSent || resendSecondsRemaining > 0"
        @click="confirmAndSendOneTimeCode"
      >
        <span class="material-icons-round mr-[6px] text-xl align-text-bottom" aria-hidden="true">mail_outline</span>
        {{ resendSecondsRemaining > 0 ? `Resend in ${resendSecondsRemaining}s` : (otcCodeRequested ? 'Resend Code' : 'Send One-Time Code') }}
      </GbButton>
    </div>

    <GbInlineMessage v-if="otcNotice" gbTheme="primary" class="my-2 justify-center">
      {{ otcNotice }}
    </GbInlineMessage>
    <GbInlineMessage v-if="otcError" gbTheme="red" class="my-2 justify-center">
      {{ otcError }}
    </GbInlineMessage>

    <div v-if="otcCodeRequested" class="otc-verify-actions flex justify-center items-end gap-3">
      <InputText
        v-model="otcCode"
        class="my-2 uppercase min-w-[10em] tracking-widest"
        maxlength="10"
        placeholder="Enter code"
        autocomplete="one-time-code"
        aria-label="One-time code"
      />

      <GbButton
        type="button"
        label="Verify Code"
        gbTheme="greenPrimary"
        size="large"
        class="my-2"
        :disabled="otcVerifySent || !otcCode"
        @click="verifyOneTimeCode"
      />
    </div>

  </div>

  <ConfirmDialog group="login-modal-otc" class="w-auto md:w-[38rem]">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col rounded-md bg-surface-50">
        <div class="mt-2 flex justify-between border-b border-gray-300 p-3">
          <span class="block pl-3 text-xl font-medium">Send One-Time Code</span>
          <GbButton
            size="small"
            rounded
            :icon-only="true"
            aria-label="close dialog"
            gbTheme="grayTertiary"
            class="mr-4 self-start"
            @click="rejectCallback"
          >
            <span class="material-icons-round">close</span>
          </GbButton>
        </div>
        <p class="py-5 pl-7 pr-12 text-gray-700">{{ message.message }}</p>
        <div class="flex items-center justify-end gap-2 border-t border-gray-300 p-4">
          <GbButton label="Cancel" @click="rejectCallback" gbTheme="grayOutlineSecondary" />
          <GbButton label="Send Code" @click="acceptCallback" gbTheme="greenPrimary" />
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>
